import { jsPDF } from 'jspdf';
import * as XLSX from '@redoper1/xlsx-js-style';

const text = (value) => String(value ?? '').trim();
const formatDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : String(value);
};
const safeFilename = (value) => text(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9._-]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '') || 'planos-de-aula';

const planItems = (plan) => {
  const fallbackNucleusId = plan?.nucleusId || null;
  const fallbackNucleusName = plan?.nucleusName || '';
  const source = Array.isArray(plan?.items) && plan.items.length
    ? plan.items
    : [{ lessonTheme: plan?.lessonTheme || '', objective: plan?.objective || '' }];
  return source.map((item) => {
    const hasTopicNucleus = Object.prototype.hasOwnProperty.call(item || {}, 'nucleusId') || Object.prototype.hasOwnProperty.call(item || {}, 'nucleusName');
    return {
      ...item,
      nucleusId: hasTopicNucleus ? (item?.nucleusId || null) : fallbackNucleusId,
      nucleusName: hasTopicNucleus ? (item?.nucleusName || '') : fallbackNucleusName,
    };
  });
};

const planStatus = (plan, today) => {
  if (plan?.endDate && plan.endDate < today) return 'Concluído';
  if (plan?.startDate && plan.startDate > today) return 'Próximo';
  return 'Em andamento';
};

const audienceText = (plan, classes, parents, parentLabel) => {
  const classNames = (plan?.classIds || [])
    .map((id) => classes.find((item) => Number(item.id) === Number(id))?.name)
    .filter(Boolean);
  const parentNames = (plan?.parentIds || [])
    .map((id) => parents.find((item) => Number(item.id) === Number(id))?.name)
    .filter(Boolean);

  if (classNames.length) {
    const parts = [`Turmas: ${classNames.join(', ')}`];
    if (parentNames.length) parts.push(`${parentLabel}: ${parentNames.join(', ')}`);
    return parts.join(' | ');
  }
  if (parentNames.length) return `Todas as turmas de ${parentLabel.toLowerCase()}: ${parentNames.join(', ')}`;
  return 'Todas as turmas';
};

export const lessonPlanReportRows = ({ plans = [], classes = [], parents = [], parentLabel = 'Cursos', nucleusId = '' }) => {
  const today = new Date().toISOString().slice(0, 10);
  return plans.flatMap((plan) => planItems(plan)
    .filter((item) => !nucleusId || String(item.nucleusId || '') === String(nucleusId))
    .map((item, index) => ({
      period: `${formatDate(plan.startDate)} a ${formatDate(plan.endDate)}`,
      responsible: text(plan.responsibleName) || 'Não informado',
      audience: audienceText(plan, classes, parents, parentLabel),
      theme: text(item.lessonTheme) || '—',
      objective: text(item.objective) || '—',
      status: planStatus(plan, today),
      nucleus: text(item.nucleusName),
      topicNumber: index + 1,
    })));
};

export function downloadLessonPlanPdf({ plans = [], classes = [], parents = [], parentLabel = 'Cursos', includeNucleus = false, nucleusId = '' }) {
  const rows = lessonPlanReportRows({ plans, classes, parents, parentLabel, nucleusId });
  const showNucleus = Boolean(includeNucleus && rows.some((row) => row.nucleus));
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const margin = 10;
  const footerY = height - 8;
  const columns = [
    { key: 'period', label: 'Período', width: showNucleus ? 27 : 30 },
    { key: 'responsible', label: 'Responsável', width: showNucleus ? 31 : 36 },
    ...(showNucleus ? [{ key: 'nucleus', label: 'Núcleo', width: 35 }] : []),
    { key: 'audience', label: 'Aplicação', width: showNucleus ? 48 : 58 },
    { key: 'theme', label: 'Tema', width: showNucleus ? 47 : 55 },
    { key: 'objective', label: 'Objetivo', width: showNucleus ? 63 : 72 },
    { key: 'status', label: 'Status', width: 22 },
  ];

  const drawHeader = () => {
    doc.setFillColor(10, 47, 103);
    doc.rect(0, 0, width, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('RELATÓRIO DE PLANOS DE AULA', margin, 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text(`${rows.length} tema(s) em ${plans.length} plano(s) • Gerado em ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date())}`, margin, 19);
    doc.setFillColor(235, 243, 252);
    doc.setTextColor(22, 57, 96);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.8);
    let x = margin;
    columns.forEach((column) => {
      doc.rect(x, 34, column.width, 8, 'F');
      doc.text(column.label, x + 1.5, 39.3, { maxWidth: column.width - 3 });
      x += column.width;
    });
    return 42;
  };

  let y = drawHeader();
  rows.forEach((row, rowIndex) => {
    const cells = columns.map((column) => doc.splitTextToSize(text(row[column.key]) || '—', column.width - 3));
    const lineCount = Math.max(1, ...cells.map((cell) => cell.length));
    const rowHeight = Math.max(8, lineCount * 3.3 + 3.5);

    if (y + rowHeight > footerY - 5) {
      doc.addPage('a4', 'landscape');
      y = drawHeader();
    }

    if (rowIndex % 2 === 1) {
      doc.setFillColor(249, 251, 254);
      doc.rect(margin, y, columns.reduce((sum, column) => sum + column.width, 0), rowHeight, 'F');
    }

    doc.setDrawColor(218, 228, 240);
    doc.setTextColor(31, 58, 91);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.4);
    let x = margin;
    cells.forEach((cell, index) => {
      doc.text(cell, x + 1.5, y + 4.4);
      doc.line(x, y, x, y + rowHeight);
      x += columns[index].width;
    });
    doc.line(x, y, x, y + rowHeight);
    doc.line(margin, y + rowHeight, x, y + rowHeight);
    y += rowHeight;
  });

  if (!rows.length) {
    doc.setTextColor(100, 120, 145);
    doc.setFontSize(9);
    doc.text('Nenhum plano encontrado para os filtros atuais.', margin, y + 10);
  }

  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(218, 228, 240);
    doc.line(margin, footerY - 3, width - margin, footerY - 3);
    doc.setTextColor(105, 123, 145);
    doc.setFontSize(6.5);
    doc.text('Relatório de Planos de Aula • iBPM Nash', margin, footerY);
    doc.text(`Página ${page} de ${pages}`, width - margin, footerY, { align: 'right' });
  }

  doc.save(`${safeFilename('relatorio-planos-de-aula')}.pdf`);
}

export function downloadLessonPlanXlsx({ plans = [], classes = [], parents = [], parentLabel = 'Cursos', includeNucleus = false, nucleusId = '' }) {
  const rows = lessonPlanReportRows({ plans, classes, parents, parentLabel, nucleusId });
  const showNucleus = Boolean(includeNucleus && rows.some((row) => row.nucleus));
  const data = rows.map((row) => ({
    'Período': row.period,
    'Responsável': row.responsible,
    ...(showNucleus ? { 'Núcleo': row.nucleus || '—' } : {}),
    'Aplicação': row.audience,
    'Tema': row.theme,
    'Objetivo': row.objective,
    'Status': row.status,
  }));

  const worksheet = XLSX.utils.json_to_sheet(data.length ? data : [{
    'Período': '',
    'Responsável': '',
    ...(showNucleus ? { 'Núcleo': '' } : {}),
    'Aplicação': '',
    'Tema': '',
    'Objetivo': '',
    'Status': '',
  }]);
  worksheet['!cols'] = showNucleus
    ? [{ wch: 23 }, { wch: 26 }, { wch: 28 }, { wch: 42 }, { wch: 36 }, { wch: 48 }, { wch: 16 }]
    : [{ wch: 23 }, { wch: 28 }, { wch: 48 }, { wch: 38 }, { wch: 50 }, { wch: 16 }];

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  for (let col = range.s.c; col <= range.e.c; col += 1) {
    const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: col })];
    if (!cell) continue;
    cell.s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '0A2F67' } },
      alignment: { vertical: 'center' },
    };
  }
  for (let row = 1; row <= range.e.r; row += 1) {
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
      if (!cell) continue;
      cell.s = {
        alignment: { vertical: 'top', wrapText: true },
        fill: row % 2 === 0 ? { fgColor: { rgb: 'F7FAFE' } } : undefined,
      };
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Planos de Aula');
  XLSX.writeFile(workbook, `${safeFilename('relatorio-planos-de-aula')}.xlsx`);
}
